import { useEffect, useState, useCallback } from "react";
import {
  Users, ShieldCheck, Activity, Plus, KeyRound, X, Loader2, Circle, CheckCircle2,
  Server, Database, Sparkles, Radio, GitCommit, Bell, Clock, Zap, Mail, Smartphone, Send, UserCog,
  Trash2, RefreshCw, ShieldAlert, Download, FileCheck2, Timer, DatabaseZap, Save, Lock, LogIn,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const ROLE_LABELS = {
  admin: "Administrator", executive: "Executive", program_manager: "Program Manager",
  nurse: "Nurse", dsp: "DSP", clinician: "Clinician", compliance: "Compliance", technician: "Technician",
};

const TABS = [
  { id: "users", label: "Users", icon: Users },
  { id: "access", label: "Role Access", icon: ShieldCheck },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "email", label: "Email", icon: Mail },
  { id: "governance", label: "Security & Governance", icon: ShieldAlert },
  { id: "system", label: "System Health", icon: Activity },
];

export default function Administration() {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  const isAdmin = user?.role === "admin";
  const canView = user?.role === "admin" || user?.role === "executive";

  if (!canView) {
    return (
      <div>
        <PageHeader overline="Workspace" title="Administration & Settings" description="Enterprise administration." />
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm" data-testid="admin-no-access">
          <ShieldCheck className="mx-auto mb-3 text-muted-foreground" size={32} />
          <div className="text-sm font-semibold">Restricted area</div>
          <p className="mt-1 text-sm text-muted-foreground">Administration is available to administrators and executives only.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader overline="Workspace · Enterprise Administration" title="Administration & Settings"
        description="Manage users, per-role module access, individual overrides and platform health." />
      <div className="mb-5 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon; const on = tab === t.id;
          return (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={cn("flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors",
                on ? "bg-[#155EEF] text-white shadow" : "bg-card text-muted-foreground hover:text-foreground")}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "users" && <UsersTab isAdmin={isAdmin} />}
      {tab === "access" && <AccessTab isAdmin={isAdmin} />}
      {tab === "notifications" && <NotificationsTab isAdmin={isAdmin} />}
      {tab === "email" && <EmailTab isAdmin={isAdmin} />}
      {tab === "governance" && <GovernanceTab isAdmin={isAdmin} />}
      {tab === "system" && <SystemTab />}
    </div>
  );
}

/* ------------------------------ Users ------------------------------ */
function UsersTab({ isAdmin }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [overrideUser, setOverrideUser] = useState(null);
  const [modules, setModules] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);
  useEffect(() => { load(); api.get("/admin/module-access").then((r) => setModules(r.data.modules)).catch(() => {}); }, [load]);

  const patch = async (u, body) => {
    try { const { data } = await api.patch(`/admin/users/${u.id}`, body); setUsers((us) => us.map((x) => (x.id === u.id ? data : x))); toast.success("User updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.name}`, "");
    if (!pw) return;
    try { await api.post(`/admin/users/${u.id}/reset-password`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); setUsers((us) => us.filter((x) => x.id !== u.id)); toast.success("User deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!users) return <Spinner />;

  return (
    <div>
      {isAdmin && (
        <div className="mb-4 flex justify-end">
          <button data-testid="add-user-btn" onClick={() => setAddOpen(true)}
            className="flex items-center gap-2 rounded-md bg-[#155EEF] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#0F4FD1]">
            <Plus size={16} /> Add User
          </button>
        </div>
      )}
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-muted/70">
            <tr>{["User", "Role", "Status", "Actions"].map((h) => <th key={h} className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-muted-foreground">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="align-middle hover:bg-muted/30">
                <td className="px-4 py-3"><div className="font-semibold">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                <td className="px-4 py-3">
                  {isAdmin ? (
                    <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => patch(u, { role: e.target.value })}
                      className="rounded-md border bg-background px-2 py-1 text-sm outline-none focus:border-[#155EEF]">
                      {Object.keys(ROLE_LABELS).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  ) : <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">{ROLE_LABELS[u.role] || u.role}</span>}
                </td>
                <td className="px-4 py-3">
                  <button data-testid={`active-toggle-${u.id}`} disabled={!isAdmin} onClick={() => patch(u, { active: !u.active })}
                    className={cn("flex items-center gap-1.5 text-xs font-semibold", u.active ? "text-[#12B76A]" : "text-muted-foreground")}>
                    <Circle size={9} className={u.active ? "fill-[#12B76A]" : "fill-muted-foreground"} /> {u.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button data-testid={`overrides-btn-${u.id}`} onClick={() => setOverrideUser(u)}
                        className="rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:border-[#155EEF] hover:text-[#155EEF]">Access</button>
                      <button data-testid={`reset-pw-btn-${u.id}`} onClick={() => resetPw(u)}
                        className="flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:border-[#F79009] hover:text-[#F79009]"><KeyRound size={13} /> Reset</button>
                      {u.email !== me?.email && (
                        <button data-testid={`delete-user-btn-${u.id}`} onClick={() => removeUser(u)}
                          className="flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:border-[#F04438] hover:text-[#F04438]"><Trash2 size={13} /></button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && <AddUserModal onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} />}
      {overrideUser && <OverrideModal user={overrideUser} modules={modules} onClose={() => setOverrideUser(null)}
        onSaved={(data) => { setUsers((us) => us.map((x) => (x.id === data.id ? data : x))); setOverrideUser(null); }} />}
    </div>
  );
}

function AddUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "dsp", password: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await api.post("/admin/users", form); toast.success("User created"); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create user"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Add User" onClose={onClose} testid="add-user-modal">
      <div className="space-y-3">
        <Field label="Full name"><input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
        <Field label="Email"><input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
        <Field label="Role">
          <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp}>
            {Object.keys(ROLE_LABELS).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </Field>
        <Field label="Temporary password"><input data-testid="new-user-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} /></Field>
      </div>
      <button data-testid="save-new-user-btn" disabled={saving || !form.email || !form.name || form.password.length < 6} onClick={submit}
        className="mt-5 w-full rounded-md bg-[#155EEF] py-2.5 text-sm font-semibold text-white hover:bg-[#0F4FD1] disabled:opacity-50">
        {saving ? "Creating…" : "Create User"}
      </button>
    </Modal>
  );
}

function OverrideModal({ user, modules, onClose, onSaved }) {
  const [grant, setGrant] = useState(user.module_grant || []);
  const [revoke, setRevoke] = useState(user.module_revoke || []);
  const toggle = (list, set, n) => set(list.includes(n) ? list.filter((x) => x !== n) : [...list, n]);
  const save = async () => {
    try { const { data } = await api.patch(`/admin/users/${user.id}`, { module_grant: grant, module_revoke: revoke }); toast.success("Overrides saved"); onSaved(data); }
    catch (e) { toast.error("Save failed"); }
  };
  return (
    <Modal title={`Module Access — ${user.name}`} onClose={onClose} testid="override-modal">
      <p className="mb-3 text-xs text-muted-foreground">Grant modules beyond this user's role, or revoke modules their role normally allows.</p>
      <div className="careiq-scroll max-h-80 space-y-2 overflow-y-auto">
        {modules.map((m) => (
          <div key={m.n} className="flex items-center justify-between gap-2 rounded-lg border p-2.5 text-sm">
            <span className="min-w-0 flex-1 truncate">{m.n}. {m.title}</span>
            <button data-testid={`grant-${user.id}-${m.n}`} onClick={() => toggle(grant, setGrant, m.n)}
              className={cn("rounded-md border px-2 py-1 text-xs font-medium", grant.includes(m.n) ? "border-[#12B76A] bg-[#12B76A]/10 text-[#12B76A]" : "text-muted-foreground")}>Grant</button>
            <button data-testid={`revoke-${user.id}-${m.n}`} onClick={() => toggle(revoke, setRevoke, m.n)}
              className={cn("rounded-md border px-2 py-1 text-xs font-medium", revoke.includes(m.n) ? "border-[#F04438] bg-[#F04438]/10 text-[#F04438]" : "text-muted-foreground")}>Revoke</button>
          </div>
        ))}
      </div>
      <button data-testid="save-overrides-btn" onClick={save} className="mt-4 w-full rounded-md bg-[#155EEF] py-2.5 text-sm font-semibold text-white hover:bg-[#0F4FD1]">Save Overrides</button>
    </Modal>
  );
}

/* ------------------------------ Role Access ------------------------------ */
function AccessTab({ isAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(null);
  useEffect(() => { api.get("/admin/module-access").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return <Spinner />;

  const toggle = async (role, n) => {
    if (!isAdmin) return;
    const current = cfg.roles[role] || [];
    const next = current.includes(n) ? current.filter((x) => x !== n) : [...current, n].sort((a, b) => a - b);
    setCfg({ ...cfg, roles: { ...cfg.roles, [role]: next } });
    setSaving(role);
    try { await api.put("/admin/module-access", { role, module_ids: next }); }
    catch (e) { toast.error("Could not save"); }
    finally { setSaving(null); }
  };

  const editableRoles = Object.keys(cfg.roles).filter((r) => !cfg.all_access_roles.includes(r));

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-[#155EEF]/5 p-3 text-xs text-muted-foreground">
        Administrators and executives always have access to all 12 modules. Edit which modules each other role can launch from the Command Center.
      </div>
      {editableRoles.map((role) => (
        <div key={role} data-testid={`role-access-${role}`} className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-bold">{ROLE_LABELS[role] || role}</span>
            <span className="text-xs text-muted-foreground">{saving === role ? "Saving…" : `${cfg.roles[role].length} of 12`}</span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {cfg.modules.map((m) => {
              const on = cfg.roles[role].includes(m.n);
              return (
                <button key={m.n} data-testid={`access-${role}-${m.n}`} disabled={!isAdmin} onClick={() => toggle(role, m.n)}
                  className={cn("flex items-center gap-2 rounded-lg border p-2.5 text-left text-xs transition-colors",
                    on ? "border-[#155EEF] bg-[#155EEF]/5" : "opacity-60 hover:opacity-100")}>
                  {on ? <CheckCircle2 size={15} className="shrink-0 text-[#155EEF]" /> : <Circle size={15} className="shrink-0 text-muted-foreground" />}
                  <span className="truncate">{m.n}. {m.title.replace("CAREIQ ", "")}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ System Health ------------------------------ */
function SystemTab() {
  const [health, setHealth] = useState(null);
  const [obs, setObs] = useState(null);
  useEffect(() => {
    api.get("/system/health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/system/observability").then((r) => setObs(r.data)).catch(() => {});
  }, []);
  if (!health) return <Spinner />;
  const ICONS = { "API (FastAPI)": Server, "Database (MongoDB)": Database, "AI Layer (Claude Sonnet 4.6)": Sparkles, "Event Bus": Radio };
  const m = health.metrics;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {health.services.map((s) => {
          const Icon = ICONS[s.name] || Server; const ok = s.status === "healthy";
          return (
            <div key={s.name} data-testid={`service-${s.name.split(" ")[0].toLowerCase()}`} className="rounded-xl border bg-card p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <Icon size={18} className="text-[#155EEF]" />
                <span className={cn("flex items-center gap-1 text-xs font-semibold", ok ? "text-[#12B76A]" : "text-[#F04438]")}>
                  <Circle size={8} className={ok ? "fill-[#12B76A]" : "fill-[#F04438]"} /> {s.status}
                </span>
              </div>
              <div className="mt-2 text-sm font-bold">{s.name}</div>
              <div className="text-xs text-muted-foreground">{s.detail}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {[["Users", m.users], ["Active", m.active_users], ["Events 24h", m.events_24h], ["Total Events", m.total_events], ["Audit Entries", m.audit_entries], ["Collections", m.collections]].map(([l, v]) => (
          <div key={l} className="rounded-xl border bg-card p-4 shadow-sm"><div className="text-2xl font-bold text-[#155EEF]">{v}</div><div className="mt-1 overline">{l}</div></div>
        ))}
      </div>

      {obs && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-bold"><GitCommit size={15} className="text-[#155EEF]" /> Event Stream</div>
            <div className="careiq-scroll max-h-80 divide-y overflow-y-auto">
              {obs.events.map((e) => (
                <div key={e.id} className="px-4 py-2.5 text-sm">
                  <div className="flex items-center justify-between"><span className="font-mono text-xs font-semibold text-[#155EEF]">{e.event_type}</span><span className="text-[11px] text-muted-foreground">{String(e.timestamp).slice(11, 19)}</span></div>
                  <div className="text-xs text-muted-foreground">by {e.actor}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-bold"><ShieldCheck size={15} className="text-[#12B76A]" /> Audit Log</div>
            <div className="careiq-scroll max-h-80 divide-y overflow-y-auto">
              {obs.audit.map((a) => (
                <div key={a.id} className="px-4 py-2.5 text-sm">
                  <div className="flex items-center justify-between"><span className="font-medium">{a.action}</span><span className="text-[11px] text-muted-foreground">{String(a.timestamp).slice(11, 19)}</span></div>
                  <div className="text-xs text-muted-foreground">{a.actor} · {a.entity_type}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Notifications & Escalation ------------------------------ */
function NotificationsTab({ isAdmin }) {
  const [prefs, setPrefs] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [oncall, setOncall] = useState(null);
  const [running, setRunning] = useState(null);

  const loadEsc = () => api.get("/notify/escalations").then((r) => setEscalations(r.data.items)).catch(() => {});
  useEffect(() => {
    api.get("/notify/preferences").then((r) => setPrefs(r.data)).catch(() => {});
    api.get("/notify/policy").then((r) => setPolicy(r.data)).catch(() => {});
    api.get("/email/on-call").then((r) => setOncall(r.data)).catch(() => {});
    loadEsc();
  }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    try { await api.put("/notify/preferences", next); toast.success("Preferences saved"); }
    catch (e) { toast.error("Could not save"); }
  };
  const savePolicy = async (next) => {
    setPolicy(next);
    try { await api.put("/notify/policy", next); }
    catch (e) { toast.error("Could not save policy"); }
  };
  const runJob = async (kind) => {
    setRunning(kind);
    try {
      const { data } = await api.post(`/notify/run-${kind}`);
      toast.success(kind === "escalation" ? `Escalation ran — ${data.escalated} alert(s) escalated` : "Daily digest generated");
      loadEsc();
    } catch (e) { toast.error("Run failed"); }
    finally { setRunning(null); }
  };
  const emailMe = async () => {
    setRunning("email-me");
    try { const { data } = await api.post("/email/me"); toast.success(`Digest emailed to ${data.sent_to}`); }
    catch (e) { toast.error("Email failed"); }
    finally { setRunning(null); }
  };
  const emailAdmins = async () => {
    setRunning("email-admins");
    try { const { data } = await api.post("/email/digest"); toast.success(`Digest emailed to ${data.count} recipient(s)`); }
    catch (e) { toast.error("Email failed"); }
    finally { setRunning(null); }
  };

  if (!prefs || !policy) return <Spinner />;
  const setCh = (k) => savePrefs({ ...prefs, channels: { ...prefs.channels, [k]: !prefs.channels[k] } });

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* My preferences */}
      <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="notify-prefs-card">
        <div className="mb-4 flex items-center gap-2 text-sm font-bold"><Bell size={16} className="text-[#155EEF]" /> My Notification Preferences</div>
        <div className="mb-4 space-y-2">
          <div className="text-xs font-medium text-muted-foreground">Delivery channels</div>
          {[["in_app", "In-app", Bell], ["email", "Email", Mail], ["sms", "SMS", Smartphone]].map(([k, l, Icon]) => (
            <label key={k} className="flex cursor-pointer items-center justify-between rounded-lg border bg-background p-2.5">
              <span className="flex items-center gap-2 text-sm"><Icon size={15} className="text-muted-foreground" /> {l}</span>
              <button data-testid={`channel-${k}`} onClick={() => setCh(k)}
                className={cn("relative h-6 w-11 rounded-full transition-colors", prefs.channels[k] ? "bg-[#155EEF]" : "bg-muted")}>
                <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform", prefs.channels[k] ? "translate-x-5" : "translate-x-0.5")} />
              </button>
            </label>
          ))}
        </div>
        <Field label="Only notify me for">
          <select data-testid="severity-threshold" value={prefs.severity_threshold} onChange={(e) => savePrefs({ ...prefs, severity_threshold: e.target.value })} className={inp}>
            <option value="all">All notifications</option>
            <option value="high">High &amp; Critical only</option>
            <option value="critical">Critical only</option>
          </select>
        </Field>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Field label="Quiet hours start"><input data-testid="quiet-start" type="time" value={prefs.quiet_start} onChange={(e) => savePrefs({ ...prefs, quiet_start: e.target.value })} className={inp} /></Field>
          <Field label="Quiet hours end"><input data-testid="quiet-end" type="time" value={prefs.quiet_end} onChange={(e) => savePrefs({ ...prefs, quiet_end: e.target.value })} className={inp} /></Field>
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Clock size={13} /> During quiet hours only Critical alerts break through.</div>
        <button data-testid="email-me-btn" disabled={running} onClick={emailMe}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-[#155EEF] px-3 py-2 text-sm font-semibold text-[#155EEF] transition-colors hover:bg-[#155EEF] hover:text-white disabled:opacity-50">
          {running === "email-me" ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email me this digest now
        </button>
      </div>

      {/* Escalation policy + jobs */}
      <div className="space-y-6">
        <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="escalation-policy-card">
          <div className="mb-4 flex items-center gap-2 text-sm font-bold"><Zap size={16} className="text-[#F79009]" /> Escalation Policy {!isAdmin && <span className="text-xs font-normal text-muted-foreground">(read-only)</span>}</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Critical escalates after (min)"><input data-testid="critical-minutes" type="number" min="1" disabled={!isAdmin} value={policy.critical_minutes} onChange={(e) => savePolicy({ ...policy, critical_minutes: Number(e.target.value) })} className={inp} /></Field>
            <Field label="High escalates after (min)"><input data-testid="high-minutes" type="number" min="1" disabled={!isAdmin} value={policy.high_minutes} onChange={(e) => savePolicy({ ...policy, high_minutes: Number(e.target.value) })} className={inp} /></Field>
          </div>
          <label className="mt-3 flex cursor-pointer items-center justify-between rounded-lg border bg-background p-2.5">
            <span className="text-sm font-medium">Escalation enabled</span>
            <button data-testid="policy-enabled" disabled={!isAdmin} onClick={() => savePolicy({ ...policy, enabled: !policy.enabled })}
              className={cn("relative h-6 w-11 rounded-full transition-colors", policy.enabled ? "bg-[#12B76A]" : "bg-muted")}>
              <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform", policy.enabled ? "translate-x-5" : "translate-x-0.5")} />
            </button>
          </label>
          <label className="mt-2 flex cursor-pointer items-center justify-between rounded-lg border bg-background p-2.5">
            <span className="text-sm font-medium">Route escalations to on-call staff</span>
            <button data-testid="policy-oncall" disabled={!isAdmin} onClick={() => savePolicy({ ...policy, oncall_enabled: !policy.oncall_enabled })}
              className={cn("relative h-6 w-11 rounded-full transition-colors", policy.oncall_enabled ? "bg-[#155EEF]" : "bg-muted")}>
              <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform", policy.oncall_enabled ? "translate-x-5" : "translate-x-0.5")} />
            </button>
          </label>
          {isAdmin && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid="run-escalation-btn" disabled={running} onClick={() => runJob("escalation")}
                className="flex items-center gap-1.5 rounded-md bg-[#F79009] px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50">
                {running === "escalation" ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Run Escalation Now
              </button>
              <button data-testid="run-digest-btn" disabled={running} onClick={() => runJob("digest")}
                className="flex items-center gap-1.5 rounded-md bg-[#155EEF] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0F4FD1] disabled:opacity-50">
                {running === "digest" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Generate Daily Digest
              </button>
              <button data-testid="email-digest-btn" disabled={running} onClick={emailAdmins}
                className="flex items-center gap-1.5 rounded-md bg-[#12B76A] px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50">
                {running === "email-admins" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email Digest to Admins
              </button>
            </div>
          )}
        </div>

        <div className="rounded-xl border bg-card shadow-sm" data-testid="escalations-list">
          <div className="border-b px-4 py-3 text-sm font-bold">Recent Escalations &amp; Digests</div>
          <div className="careiq-scroll max-h-64 divide-y overflow-y-auto">
            {escalations.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Nothing escalated yet.</div>}
            {escalations.map((n) => (
              <div key={n.id} className="flex items-start gap-2.5 px-4 py-2.5">
                <span className={cn("mt-1 h-2 w-2 shrink-0 rounded-full", n.kind === "digest" ? "bg-[#155EEF]" : "bg-[#F04438]")} />
                <div className="min-w-0 flex-1"><div className="truncate text-[13px] font-medium">{n.title}</div><div className="truncate text-xs text-muted-foreground">{n.detail}</div></div>
                <span className="shrink-0 text-[10px] text-muted-foreground">{String(n.created_at).slice(11, 16)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border bg-card shadow-sm" data-testid="oncall-panel">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-bold"><UserCog size={15} className="text-[#155EEF]" /> On Call Right Now</span>
            {oncall && <span className="rounded-full bg-[#155EEF]/10 px-2 py-0.5 text-[11px] font-semibold text-[#155EEF]">{oncall.shift} shift · {oncall.count}</span>}
          </div>
          <div className="careiq-scroll max-h-48 divide-y overflow-y-auto">
            {(!oncall || oncall.count === 0) && <div className="px-4 py-5 text-center text-sm text-muted-foreground">No staff on shift right now.</div>}
            {oncall?.on_call?.map((o) => (
              <div key={o.email} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <div className="min-w-0"><div className="truncate font-medium">{o.name}</div><div className="truncate text-xs text-muted-foreground">{o.program}</div></div>
                <span className="shrink-0 text-[11px] text-muted-foreground">{o.email}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Email: recipient groups & delivery log ------------------------------ */
const GROUP_LABELS = {
  digest: "Daily Digest", escalation: "Critical Escalations", board_pack: "Weekly Board Pack",
  evidence_pack: "Monthly Evidence Pack",
};
const LOG_STATUS = { sent: "#12B76A", failed: "#F04438" };

function EmailTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [log, setLog] = useState([]);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(() => {
    api.get("/email/recipient-groups").then((r) => setData(r.data)).catch(() => setData({ groups: {}, users: [], fallback: [] }));
    api.get("/email/log").then((r) => setLog(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setRetrying(id);
    try { await api.post(`/email/log/${id}/retry`); toast.success("Email resent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
    finally { setRetrying(null); }
  };

  const toggle = async (type, email) => {
    if (!isAdmin) return;
    const current = data.groups[type] || [];
    const next = current.includes(email) ? current.filter((x) => x !== email) : [...current, email];
    setData({ ...data, groups: { ...data.groups, [type]: next } });
    try { await api.put("/email/recipient-groups", { type, emails: next }); }
    catch (e) { toast.error("Could not save recipients"); }
  };

  if (!data) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-lg border bg-[#155EEF]/5 p-3 text-xs text-muted-foreground">
          Choose which people receive each email type. Recipients must be existing CAREIQ users. Leave a group empty to default to all administrators.
        </div>
        {Object.keys(GROUP_LABELS).map((type) => (
          <div key={type} data-testid={`recipient-group-${type}`} className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-bold"><Mail size={15} className="text-[#155EEF]" /> {GROUP_LABELS[type]}</span>
              <span className="text-xs text-muted-foreground">{(data.groups[type] || []).length === 0 ? "All admins (default)" : `${data.groups[type].length} selected`}</span>
            </div>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {data.users.map((u) => {
                const on = (data.groups[type] || []).includes(u.email);
                return (
                  <button key={u.email} data-testid={`recipient-${type}-${u.email}`} disabled={!isAdmin} onClick={() => toggle(type, u.email)}
                    className={cn("flex items-center gap-2 rounded-lg border p-2 text-left text-xs transition-colors", on ? "border-[#155EEF] bg-[#155EEF]/5" : "opacity-70 hover:opacity-100")}>
                    {on ? <CheckCircle2 size={15} className="shrink-0 text-[#155EEF]" /> : <Circle size={15} className="shrink-0 text-muted-foreground" />}
                    <span className="min-w-0 truncate"><span className="font-medium">{u.name}</span><span className="text-muted-foreground"> · {ROLE_LABELS[u.role] || u.role}</span></span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border bg-card shadow-sm" data-testid="email-log">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="flex items-center gap-2 text-sm font-bold"><Send size={15} className="text-[#12B76A]" /> Email Delivery Log</span>
          <button data-testid="refresh-log-btn" onClick={load} className="text-xs font-medium text-[#155EEF] hover:underline">Refresh</button>
        </div>
        <div className="careiq-scroll max-h-[560px] divide-y overflow-y-auto">
          {log.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">No emails sent yet.</div>}
          {log.map((e) => (
            <div key={e.id} className="flex items-start gap-2.5 px-4 py-2.5">
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: LOG_STATUS[e.status] || "#94A3B8" }} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[13px]"><span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">{e.kind}</span><span className="truncate">{e.to}</span></div>
                <div className="truncate text-xs text-muted-foreground">{e.error ? `Failed — ${e.error}` : e.subject}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {isAdmin && (
                  <button data-testid={`retry-email-${e.id}`} disabled={retrying === e.id} onClick={() => retry(e.id)}
                    title="Resend" className="text-muted-foreground transition-colors hover:text-[#155EEF] disabled:opacity-50">
                    {retrying === e.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  </button>
                )}
                <span className="text-[10px] text-muted-foreground">{String(e.created_at).slice(11, 16)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Security & Governance ------------------------------ */
const SEC_FIELDS = [
  { key: "session_minutes", label: "Session timeout (min)", min: 5, max: 1440, hint: "How long a login stays valid before re-authentication." },
  { key: "lockout_threshold", label: "Failed logins before lockout", min: 1, max: 20, hint: "Consecutive failures that trigger a temporary lock." },
  { key: "lockout_minutes", label: "Lockout duration (min)", min: 1, max: 1440, hint: "How long the account/IP stays locked." },
  { key: "password_min_length", label: "Minimum password length", min: 6, max: 64, hint: "Enforced on new users and password resets." },
  { key: "ip_block_threshold", label: "Auto-block IP after N failures", min: 3, max: 100, hint: "Failed sign-ins from one network (any account) within the lockout window before the whole IP is blocked." },
];
const RET_FIELDS = [
  { key: "audit_days", label: "Audit log (days)", min: 30, max: 3650 },
  { key: "event_days", label: "Event stream (days)", min: 7, max: 3650 },
  { key: "notification_days", label: "Notifications (days)", min: 7, max: 3650 },
  { key: "email_log_days", label: "Email delivery log (days)", min: 7, max: 3650 },
];

function GovernanceTab({ isAdmin }) {
  const [settings, setSettings] = useState(null);
  const [sec, setSec] = useState(null);
  const [ret, setRet] = useState(null);
  const [savingSec, setSavingSec] = useState(false);
  const [savingRet, setSavingRet] = useState(false);
  const [purging, setPurging] = useState(false);
  const [verify, setVerify] = useState(null);
  const [checking, setChecking] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [access, setAccess] = useState(null);
  const [blocked, setBlocked] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/settings").then((r) => { setSettings(r.data); setSec(r.data.security); setRet(r.data.retention); }).catch(() => setSettings({}));
    api.get("/admin/access-log").then((r) => setAccess(r.data)).catch(() => {});
    api.get("/admin/blocked-ips").then((r) => setBlocked(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSec = async () => {
    setSavingSec(true);
    try { const { data } = await api.put("/admin/settings/security", sec); setSec(data.security); toast.success("Security settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingSec(false); }
  };
  const saveRet = async () => {
    setSavingRet(true);
    try { const { data } = await api.put("/admin/settings/retention", ret); setRet(data.retention); toast.success("Retention policy saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingRet(false); }
  };
  const runPurge = async () => {
    if (!window.confirm("Purge records older than the retention window now? This permanently deletes aged events, notifications and email logs.")) return;
    setPurging(true);
    try { const { data } = await api.post("/admin/retention/run"); const n = Object.values(data.purged).reduce((a, b) => a + b, 0); toast.success(`Retention purge complete — ${n} record(s) removed`); }
    catch (e) { toast.error("Purge failed"); }
    finally { setPurging(false); }
  };
  const runVerify = async () => {
    setChecking(true);
    try { const { data } = await api.get("/admin/audit/verify"); setVerify(data); toast[data.ok ? "success" : "error"](data.ok ? "Audit chain intact" : `Tampering detected at #${data.broken_at_seq}`); }
    catch (e) { toast.error("Verification failed"); }
    finally { setChecking(false); }
  };
  const exportAudit = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/admin/audit/export?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `careiq-audit-log.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Audit log exported (${fmt.toUpperCase()})`);
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(null); }
  };
  const exportPack = async () => {
    setExporting("pack");
    try {
      const res = await api.get("/admin/evidence-pack/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "careiq-compliance-evidence-pack.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Compliance Evidence Pack exported");
    } catch (e) { toast.error("Evidence Pack export failed"); }
    finally { setExporting(null); }
  };
  const emailPack = async () => {
    setExporting("email");
    try { const { data } = await api.post("/email/evidence-pack"); toast.success(`Evidence Pack emailed to ${data.count} recipient(s)`); }
    catch (e) { toast.error("Email failed"); }
    finally { setExporting(null); }
  };
  const unblockIp = async (ip) => {
    try { await api.post(`/admin/blocked-ips/${encodeURIComponent(ip)}/unblock`); toast.success(`Unblocked ${ip}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Unblock failed"); }
  };

  if (!sec || !ret) return <Spinner />;
  const ACC_STATUS = { success: "#12B76A", failed: "#F79009", locked: "#F04438" };

  return (
    <div className="space-y-6">
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Security & session */}
      <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="security-settings-card">
        <div className="mb-4 flex items-center gap-2 text-sm font-bold"><Lock size={16} className="text-[#155EEF]" /> Security & Session {!isAdmin && <span className="text-xs font-normal text-muted-foreground">(read-only)</span>}</div>
        <div className="space-y-3">
          {SEC_FIELDS.map((f) => (
            <div key={f.key}>
              <Field label={f.label}>
                <input data-testid={`sec-${f.key}`} type="number" min={f.min} max={f.max} disabled={!isAdmin}
                  value={sec[f.key]} onChange={(e) => setSec({ ...sec, [f.key]: Number(e.target.value) })} className={inp} />
              </Field>
              <div className="mt-1 text-[11px] text-muted-foreground">{f.hint}</div>
            </div>
          ))}
        </div>
        {isAdmin && (
          <button data-testid="save-security-btn" disabled={savingSec} onClick={saveSec}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-[#155EEF] py-2.5 text-sm font-semibold text-white hover:bg-[#0F4FD1] disabled:opacity-50">
            {savingSec ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Security Settings
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Data retention */}
        <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="retention-settings-card">
          <div className="mb-4 flex items-center gap-2 text-sm font-bold"><Timer size={16} className="text-[#F79009]" /> Data Retention Policy {!isAdmin && <span className="text-xs font-normal text-muted-foreground">(read-only)</span>}</div>
          <div className="grid grid-cols-2 gap-3">
            {RET_FIELDS.map((f) => (
              <Field key={f.key} label={f.label}>
                <input data-testid={`ret-${f.key}`} type="number" min={f.min} max={f.max} disabled={!isAdmin}
                  value={ret[f.key]} onChange={(e) => setRet({ ...ret, [f.key]: Number(e.target.value) })} className={inp} />
              </Field>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground"><DatabaseZap size={13} /> Audit logs are retained for compliance and are never fully purged.</div>
          {isAdmin && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid="save-retention-btn" disabled={savingRet} onClick={saveRet}
                className="flex items-center gap-1.5 rounded-md bg-[#155EEF] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0F4FD1] disabled:opacity-50">
                {savingRet ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Policy
              </button>
              <button data-testid="run-purge-btn" disabled={purging} onClick={runPurge}
                className="flex items-center gap-1.5 rounded-md bg-[#F04438] px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50">
                {purging ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Run Purge Now
              </button>
            </div>
          )}
        </div>

        {/* Audit integrity */}
        <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="audit-integrity-card">
          <div className="mb-3 flex items-center gap-2 text-sm font-bold"><FileCheck2 size={16} className="text-[#12B76A]" /> Audit Log Integrity</div>
          <p className="mb-3 text-xs text-muted-foreground">Every audit entry is chained with a SHA-256 hash of the prior entry, making the log tamper-evident. Verify the chain or export the full log for external auditors.</p>
          {verify && (
            <div data-testid="verify-result" className={cn("mb-3 flex items-center gap-2 rounded-lg border p-3 text-sm font-semibold",
              verify.ok ? "border-[#12B76A]/40 bg-[#12B76A]/10 text-[#12B76A]" : "border-[#F04438]/40 bg-[#F04438]/10 text-[#F04438]")}>
              {verify.ok ? <CheckCircle2 size={16} /> : <ShieldAlert size={16} />}
              {verify.ok ? `Chain intact — ${verify.verified_entries} entries verified (up to #${verify.last_seq})` : `Tampering detected at entry #${verify.broken_at_seq}`}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button data-testid="verify-audit-btn" disabled={checking} onClick={runVerify}
              className="flex items-center gap-1.5 rounded-md bg-[#12B76A] px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50">
              {checking ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Verify Integrity
            </button>
            <button data-testid="export-audit-csv-btn" disabled={exporting} onClick={() => exportAudit("csv")}
              className="flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold transition-colors hover:border-[#155EEF] hover:text-[#155EEF] disabled:opacity-50">
              {exporting === "csv" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export CSV
            </button>
            <button data-testid="export-audit-json-btn" disabled={exporting} onClick={() => exportAudit("json")}
              className="flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold transition-colors hover:border-[#155EEF] hover:text-[#155EEF] disabled:opacity-50">
              {exporting === "json" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export JSON
            </button>
          </div>
          <button data-testid="evidence-pack-btn" disabled={exporting} onClick={exportPack}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-[#155EEF] py-2.5 text-sm font-semibold text-white hover:bg-[#0F4FD1] disabled:opacity-50">
            {exporting === "pack" ? <Loader2 size={15} className="animate-spin" /> : <FileCheck2 size={15} />} Download Compliance Evidence Pack (PDF)
          </button>
          {isAdmin && (
            <button data-testid="email-evidence-pack-btn" disabled={exporting} onClick={emailPack}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-[#155EEF] py-2 text-xs font-semibold text-[#155EEF] transition-colors hover:bg-[#155EEF] hover:text-white disabled:opacity-50">
              {exporting === "email" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email Evidence Pack to recipients
            </button>
          )}
        </div>

        {/* Blocked IPs */}
        <div className="rounded-xl border bg-card shadow-sm" data-testid="blocked-ips-card">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-bold"><ShieldAlert size={15} className="text-[#F04438]" /> Auto-Blocked IPs</span>
            <span className="text-xs text-muted-foreground">{blocked ? `${blocked.count} active` : "…"}</span>
          </div>
          <div className="careiq-scroll max-h-56 divide-y overflow-y-auto">
            {(!blocked || blocked.count === 0) && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No IPs are currently blocked.</div>}
            {blocked?.items?.map((b) => (
              <div key={b.ip} data-testid={`blocked-ip-${b.ip}`} className="flex items-center gap-2.5 px-4 py-2.5 text-sm">
                <span className="h-2 w-2 shrink-0 rounded-full bg-[#F04438]" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono font-semibold">{b.ip}</div>
                  <div className="truncate text-xs text-muted-foreground">{b.reason} · until {String(b.until).slice(11, 16)}</div>
                </div>
                {isAdmin && (
                  <button data-testid={`unblock-ip-${b.ip}`} onClick={() => unblockIp(b.ip)}
                    className="shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:border-[#12B76A] hover:text-[#12B76A]">Unblock</button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Access & sign-in activity */}
    <div className="rounded-xl border bg-card shadow-sm" data-testid="access-log-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <span className="flex items-center gap-2 text-sm font-bold"><LogIn size={16} className="text-[#155EEF]" /> Access & Sign-In Activity</span>
        <div className="flex items-center gap-2">
          {access && (
            <div className="flex items-center gap-2 text-[11px] font-semibold">
              <span className="rounded-full bg-[#12B76A]/10 px-2 py-0.5 text-[#12B76A]" data-testid="access-success-24h">{access.summary.success_24h} ok</span>
              <span className="rounded-full bg-[#F79009]/10 px-2 py-0.5 text-[#F79009]" data-testid="access-failed-24h">{access.summary.failed_24h} failed</span>
              <span className="rounded-full bg-[#F04438]/10 px-2 py-0.5 text-[#F04438]" data-testid="access-locked-24h">{access.summary.locked_24h} locked</span>
              <span className="hidden text-muted-foreground sm:inline">· 24h</span>
            </div>
          )}
          <button data-testid="refresh-access-btn" onClick={load} className="text-xs font-medium text-[#155EEF] hover:underline">Refresh</button>
        </div>
      </div>
      <div className="careiq-scroll max-h-80 divide-y overflow-y-auto">
        {(!access || access.items.length === 0) && <div className="px-4 py-8 text-center text-sm text-muted-foreground">No sign-in activity recorded yet.</div>}
        {access?.items?.map((a, i) => (
          <div key={i} className="flex items-center gap-2.5 px-4 py-2.5 text-sm">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: ACC_STATUS[a.status] || "#94A3B8" }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><span className="truncate font-medium">{a.email}</span>
                <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={{ backgroundColor: (ACC_STATUS[a.status] || "#94A3B8") + "1a", color: ACC_STATUS[a.status] || "#94A3B8" }}>{a.status}</span>
              </div>
              <div className="truncate text-xs text-muted-foreground">IP {a.ip}{a.detail ? ` · ${a.detail}` : ""}</div>
            </div>
            <span className="shrink-0 text-[10px] text-muted-foreground">{String(a.timestamp).slice(5, 16).replace("T", " ")}</span>
          </div>
        ))}
      </div>
    </div>
    </div>
  );
}

/* ------------------------------ shared bits ------------------------------ */
const inp = "h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:border-[#155EEF] focus:ring-2 focus:ring-[#155EEF]/20";
const Spinner = () => <div className="flex h-40 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;
const Field = ({ label, children }) => <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}</label>;

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div data-testid={testid} className="careiq-scroll relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border bg-card p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold">{title}</h3>
          <button data-testid="modal-close-btn" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
